import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'
import { browserTimezone } from '@/lib/actModes'

interface Child {
  id: number
  displayName: string
}

interface ReminderPref {
  id?: number
  childId: number
  emailAddress?: string
  enabled: boolean
  hourOfDay?: number
  timezone?: string | null
}

const HOURS = [7, 8, 9, 12, 15, 16, 17, 18, 19, 20]

function hourLabel(hour: number) {
  if (hour === 12) return '12pm'
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`
}

/**
 * Sets up the daily ACT nudge that goes to the kid's own inbox. Parent-managed
 * on purpose — the address is entered here, used only for these reminders, and
 * can be removed in one click.
 */
function ChildReminder({ child }: { child: Child }) {
  const [pref, setPref] = useState<ReminderPref | null>(null)
  const [email, setEmail] = useState('')
  const [hour, setHour] = useState(17)
  const [enabled, setEnabled] = useState(true)
  const [status, setStatus] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    apiFetch<ReminderPref>(`/act/reminders?childId=${child.id}`)
      .then(data => {
        setPref(data)
        if (data?.emailAddress) setEmail(data.emailAddress)
        if (typeof data?.hourOfDay === 'number') setHour(data.hourOfDay)
        setEnabled(Boolean(data?.enabled))
      })
      .catch(() => {})
  }, [child.id])

  const save = async () => {
    setSaving(true)
    setStatus(null)
    try {
      const saved = await apiFetch<ReminderPref>('/act/reminders', {
        method: 'PUT',
        body: JSON.stringify({
          childId: child.id,
          emailAddress: email.trim(),
          enabled,
          hourOfDay: hour,
          timezone: pref?.timezone || browserTimezone(),
        }),
      })
      setPref(saved)
      setStatus('Saved')
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    setSaving(true)
    setStatus(null)
    try {
      await apiFetch(`/act/reminders?childId=${child.id}`, { method: 'DELETE' })
      setPref(null)
      setEmail('')
      setEnabled(true)
      setStatus('Removed')
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not remove')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="p-4 rounded-lg border border-border space-y-3">
      <p className="font-medium">{child.displayName}</p>

      <div>
        <label className="block text-sm mb-1" htmlFor={`act-email-${child.id}`}>
          Their email address
        </label>
        <input
          id={`act-email-${child.id}`}
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="kid@example.com"
          className="w-full px-3 py-2 border border-border rounded-lg min-h-[44px]"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Used only for this reminder. Nothing else is sent here.
        </p>
      </div>

      <div>
        <label className="block text-sm mb-1" htmlFor={`act-hour-${child.id}`}>
          Send at
        </label>
        <select
          id={`act-hour-${child.id}`}
          value={hour}
          onChange={e => setHour(Number(e.target.value))}
          className="w-full px-3 py-2 border border-border rounded-lg min-h-[44px]"
        >
          {HOURS.map(h => <option key={h} value={h}>{hourLabel(h)}</option>)}
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Only sends on days with no ACT questions logged yet.
        </p>
      </div>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={e => setEnabled(e.target.checked)}
          className="w-5 h-5"
        />
        <span className="text-sm">Send daily ACT reminders</span>
      </label>

      <div className="flex gap-2 items-center flex-wrap">
        <button
          onClick={save}
          disabled={saving || !email.trim()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-40 min-h-[44px]"
        >
          Save
        </button>
        {pref?.emailAddress && (
          <button
            onClick={remove}
            disabled={saving}
            className="px-4 py-2 border border-border rounded-lg font-medium hover:bg-accent transition min-h-[44px]"
          >
            Remove
          </button>
        )}
        {status && <span className="text-sm text-muted-foreground">{status}</span>}
      </div>
    </div>
  )
}

export default function ActReminderSection({ children }: { children: Child[] }) {
  if (children.length === 0) return null

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">ACT Practice Reminders</h3>
      <MagnumSays size="sm">
        I'll email them directly when they haven't logged an ACT question yet that day. Streak on the line,
        stats in the message. Leave the address blank and I stay quiet.
      </MagnumSays>
      {children.map(child => <ChildReminder key={child.id} child={child} />)}
    </div>
  )
}
